// UPS Shipping/Rating API caps each AddressLine at 35 characters. Exceeding it
// triggers UPS error 120202 "Missing or invalid ship to address line 1" -- the
// API returns this generic missing/invalid code for over-length lines, not just
// empty ones. Unlike the Name field (see ./upsName.ts) there is deliberately NO
// backend auto-clip for the street, because blindly truncating a street would
// corrupt it ("4950 Northeast Martin Luther King Junior Boulev"). Instead we
// flag the problem to the rep, propose an abbreviated rewrite, and block
// submission until the line fits -- mirroring the Name truncation warning.
export const UPS_ADDRESS_LINE_MAX = 35;

export function willUpsAddressTruncate(
  street: string | null | undefined,
  limit: number = UPS_ADDRESS_LINE_MAX,
): boolean {
  return !!street && street.trim().length > limit;
}

// USPS-standard abbreviations (Publication 28). Applied case-insensitively as
// whole words so we never mangle a substring (e.g. "Northwood" is left intact).
const STREET_ABBREVIATIONS: Record<string, string> = {
  // Directionals
  north: 'N', south: 'S', east: 'E', west: 'W',
  northeast: 'NE', northwest: 'NW', southeast: 'SE', southwest: 'SW',
  // Common street suffixes
  avenue: 'Ave', boulevard: 'Blvd', circle: 'Cir', court: 'Ct',
  drive: 'Dr', expressway: 'Expy', highway: 'Hwy', lane: 'Ln',
  parkway: 'Pkwy', place: 'Pl', road: 'Rd', square: 'Sq',
  street: 'St', terrace: 'Ter', trail: 'Trl',
  // Secondary-unit / misc
  apartment: 'Apt', building: 'Bldg', floor: 'Fl', suite: 'Ste',
  junior: 'Jr', senior: 'Sr',
};

// Best-effort shortening of an over-length street line for the rep to review.
// PREVIEW ONLY: callers must never write the result back into form state
// automatically -- the rep stays in control of the final value. The output may
// itself still exceed the limit (very long addresses), in which case the caller
// should tell the rep to shorten it further.
export function proposeUpsAddress(street: string | null | undefined): string {
  if (!street) return '';
  return street
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => {
      // Split off any trailing punctuation (e.g. "Boulevard," -> "Blvd,").
      const m = word.match(/^([A-Za-z]+)(\W*)$/);
      if (!m) return word;
      const [, core, tail] = m;
      const abbr = STREET_ABBREVIATIONS[core.toLowerCase()];
      return abbr ? abbr + tail : word;
    })
    .join(' ');
}
